import type { UploadResponse, InventoryItem, Document, DocumentType } from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://vision-inventory.suyesh.workers.dev';

class ApiClient {
  private tenantId: string | null = null;

  constructor() {
    this.tenantId = localStorage.getItem('tenantId');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.tenantId) {
      (headers as Record<string, string>)['X-Tenant-ID'] = this.tenantId;
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  setTenantId(tenantId: string | null) {
    this.tenantId = tenantId;
  }

  getTenantId(): string | null {
    return this.tenantId;
  }

  // Document upload - uses fast endpoint with edge processing
  async uploadDocument(
    file: File | Blob,
    documentType: DocumentType,
    fileName?: string,
    useFastPath: boolean = true // Default to fast path
  ): Promise<UploadResponse & { tier?: string; provider?: string }> {
    const formData = new FormData();
    formData.append('file', file, fileName || (file instanceof File ? file.name : 'camera_capture.jpg'));
    formData.append('documentType', documentType);

    const headers: HeadersInit = {};
    if (this.tenantId) {
      headers['X-Tenant-ID'] = this.tenantId;
    }

    // Use fast endpoint for edge processing, fall back to regular upload
    const endpoint = useFastPath ? '/api/documents/fast' : '/api/documents/upload';

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Legacy upload - always uses Gemini (for comparison/fallback)
  async uploadDocumentSlow(
    file: File | Blob,
    documentType: DocumentType,
    fileName?: string
  ): Promise<UploadResponse> {
    return this.uploadDocument(file, documentType, fileName, false);
  }

  // Inventory endpoints
  async getInventory(): Promise<InventoryItem[]> {
    return this.request<InventoryItem[]>('/api/inventory');
  }

  async updateInventoryItem(id: string, updates: Partial<InventoryItem>): Promise<InventoryItem> {
    return this.request<InventoryItem>(`/api/inventory/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // Document endpoints
  async getDocuments(): Promise<Document[]> {
    return this.request<Document[]>('/api/documents');
  }

  async getDocument(id: string): Promise<Document> {
    return this.request<Document>(`/api/documents/${id}`);
  }
}

export const api = new ApiClient();
